import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { GPSData } from "PosterEditor/model/poster-project";

export function extractGPSData(json: ActivityJson): GPSData | undefined {
  const elevationGain = elevationGainFromCoordinates(json);
  const times = activityTimesFromCoordinates(json);
  const distance = totalDistanceFromCoordinates(json);

  if (
    elevationGain === 0 &&
    times.endTime === undefined &&
    times.startTime === undefined
  ) {
    return undefined;
  }

  return {
    elevationGain,
    start: times.startTime,
    end: times.endTime,
    distanceMeters: distance,
  };
}

type ActivityJson = {
  allCoordinates?: Coordinate[];
  // ...other fields
};

interface ElevationGainOptions {
  /** Ignore small per-step climbs below this (meters). Default: 2 */
  minStepMeters?: number;
  /** Changes smaller than this (± meters) are treated as noise and ignored. Default: 0.5 */
  noiseBandMeters?: number;
  /** Allow small descents up to this (meters) without resetting the climb buffer. Default: 1 */
  descentForgivenessMeters?: number;
}

/**
 * Buffered elevation gain calculation: tolerates small dips and accumulates rises until they exceed the threshold.
 * Returns meters.
 */
export function elevationGainFromAllCoordinates(
  points: Coordinate[],
  opts: ElevationGainOptions = {}
): number {
  const minStep = opts.minStepMeters ?? 2; // meters; closer to Strava (baro devices)
  const noise = opts.noiseBandMeters ?? 0.05; // meters
  const forgive = opts.descentForgivenessMeters ?? 1; // meters

  let gain = 0;
  let prevElev: number | undefined;

  // Accumulates small positive rises until they clear minStep
  let climbBuffer = 0;

  for (const p of points) {
    const e = toNumberOrUndefined(p.elevation);
    if (e === undefined) continue;

    if (prevElev === undefined) {
      prevElev = e;
      continue;
    }

    let delta = e - prevElev;

    // Treat small wiggles as zero
    if (Math.abs(delta) < noise) {
      delta = 0;
    }

    if (delta > 0) {
      // going up: accumulate until it becomes a proper climb
      climbBuffer += delta;
    } else if (delta < 0) {
      // small dips shouldn't erase progress; allow some forgiveness
      const drop = Math.abs(delta);
      if (drop <= forgive) {
        // ignore minor descent entirely (do not touch buffer)
      } else {
        // larger descent eats into the buffer first
        const eaten = Math.min(climbBuffer, drop);
        climbBuffer -= eaten;
        // any remaining drop effectively resets the climb
        if (drop > eaten) {
          climbBuffer = 0;
        }
      }
    }

    // When we have accumulated a real climb, count it and reset buffer
    if (climbBuffer >= minStep) {
      gain += climbBuffer;
      climbBuffer = 0;
    }

    prevElev = e;
  }

  // optional: if activity ends mid‑climb and buffer exceeds threshold, count it
  if (climbBuffer >= minStep) {
    gain += climbBuffer;
  }

  // round to 2 decimals for nicer display
  return Math.round(gain * 100) / 100;
}

/** Convenience wrapper if you want to pass the whole JSON blob */
export function elevationGainFromCoordinates(
  json: ActivityJson,
  opts?: ElevationGainOptions
): number {
  return elevationGainFromAllCoordinates(json.allCoordinates ?? [], opts);
}

export function activityTimesFromCoordinates(json: ActivityJson): {
  startTime: Date | undefined;
  endTime: Date | undefined;
} {
  const coords = json.allCoordinates;
  if (!coords || coords.length === 0) {
    return { startTime: undefined, endTime: undefined };
  }

  let startTimeMs: number | undefined;
  let endTimeMs: number | undefined;

  for (const p of coords) {
    const t = toNumberOrUndefined(p.time);
    if (t === undefined) continue;

    if (startTimeMs === undefined || t < startTimeMs) {
      startTimeMs = t;
    }
    if (endTimeMs === undefined || t > endTimeMs) {
      endTimeMs = t;
    }
  }

  if (
    startTimeMs === undefined ||
    endTimeMs === undefined ||
    endTimeMs < startTimeMs
  ) {
    return { startTime: undefined, endTime: undefined };
  }

  return {
    startTime: new Date(startTimeMs),
    endTime: new Date(endTimeMs),
  };
}

export function totalDistanceFromCoordinates(json: ActivityJson): number {
  const coords = json.allCoordinates;
  if (!coords || coords.length < 2) return 0;

  const R = 6371000; // Earth radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  let totalMeters = 0;
  let lastLat: number | undefined;
  let lastLng: number | undefined;

  for (const p of coords) {
    const lat = toNumberOrUndefined((p as any).lat);
    const lng = toNumberOrUndefined((p as any).lng);
    if (lat === undefined || lng === undefined) continue;

    if (lastLat !== undefined && lastLng !== undefined) {
      const dLat = toRad(lat - lastLat);
      const dLon = toRad(lng - lastLng);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lastLat)) *
          Math.cos(toRad(lat)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      totalMeters += R * c; // meters
    }

    lastLat = lat;
    lastLng = lng;
  }

  // Return integer meters
  return Math.round(totalMeters);
}

// ---- helpers ----
function toNumberOrUndefined(v: unknown): number | undefined {
  if (v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
